/**
 * Capability strings — the lists, not the names.
 *
 * This file used to export 56 named constants (`ROUTES_CREATE = 'routes.create'`
 * …) under a "single source of truth" header. It was not one: nothing referenced
 * them. All 47 `can()` calls wrote the string, `resolve.js` wrote the string,
 * and `firestore.rules` has its own literals — so the constants could not have
 * caught a typo anywhere, and a reader finding them naturally assumed the
 * opposite.
 *
 * What IS load-bearing are the three lists below. `resolveGymCaps` reads them
 * and the platform admin's cap editor renders one. They live in `@sendly/shared`
 * because the client and BOTH Cloud Functions codebases resolve caps from them:
 * one module, so a cap cannot exist on one side and not the other (that was a
 * privilege bug waiting to happen while each Functions codebase carried its own
 * generated version of these lists). Order carries no meaning, grouping is for
 * readability only.
 */

/** Every gym-scoped capability. Excludes the derived `adminPanel.access`. */
export const ALL_GYM_CAPS = [
  // routes
  'routes.create', 'routes.edit', 'routes.archive', 'routes.delete', 'routes.viewTab',
  // users
  'users.view', 'users.viewProfile', 'users.changeRole', 'users.kick', 'users.ban', 'users.viewAuditLog',
  // gym profile
  'gym.editProfile', 'gym.editBranding', 'gym.editVisibility', 'gym.editGeneral',
  // gym config
  'maps.manage', 'difficulty.manage', 'holdColors.manage',
  // tv
  'tv.manage', 'tv.regenerateToken', 'tv.forceReleaseTimer',
  // stats
  'stats.viewSetterTab', 'stats.viewAdvanced',
  // competitions & series
  'competitions.manage', 'competitions.viewClimberTimeline', 'series.manage',
  // db
  'db.export', 'db.maintain',
  // billing
  'billing.view', 'billing.changeCurrency',
  // interval timer
  'intervalTimer.managePresets',
  // events (camps, workshops, training sessions)
  'events.manage', 'events.checkIn',
  // MCP server (paid add-on): read tools + the admin section / write tools + the write switch
  'mcp.read', 'mcp.write',
  // spraywall (paid add-on): wall management / problem moderation
  'spraywall.manage', 'spraywall.moderate',
];

/**
 * Sub-caps that grant `adminPanel.access` when any is present.
 *
 * Deliberately excludes `competitions.manage`, `events.manage`, and
 * `events.checkIn`: competitions and events have their own public tabs with
 * in-tab admin controls (edit/create), so a user whose only elevated cap is
 * managing competitions or events doesn't need the admin panel to do their
 * job. This keeps the admin panel reserved for gym-wide settings.
 *
 * `users.viewProfile` is excluded for the same reason — it is reachable from
 * the routes tab, so a setter can hold it via extraCaps without that alone
 * unlocking the admin panel.
 */
export const ADMIN_PANEL_SUB_CAPS = [
  'users.view', 'users.changeRole', 'users.kick', 'users.ban', 'users.viewAuditLog',
  'gym.editProfile', 'gym.editBranding', 'gym.editVisibility', 'gym.editGeneral',
  'maps.manage', 'difficulty.manage', 'holdColors.manage',
  'tv.manage', 'tv.regenerateToken', 'tv.forceReleaseTimer',
  'stats.viewAdvanced',
  'series.manage',
  'db.export', 'db.maintain',
  'billing.view', 'billing.changeCurrency',
  'mcp.read',
];

/** Every platform-scoped capability. Rendered as the platform cap editor's list. */
export const ALL_PLATFORM_CAPS = [
  'platform.gym.create', 'platform.gym.activate', 'platform.gym.delete',
  'platform.gym.editPlan', 'platform.gym.editFeatures', 'platform.gym.editBilling',
  'platform.gym.assignAdmin', 'platform.gym.adminAny',
  'platform.stats.view', 'platform.pricing.edit', 'platform.settings.edit',
  'platform.user.editCaps', 'platform.viewAuditLog',
  'platform.recap.manage', 'platform.crashLogs.view',
  'platform.viewMarketData', 'platform.users.view', 'platform.users.ban',
  'platform.mcp.use', 'platform.gym.editAddons',
];

/**
 * Platform capabilities that are useless without another one: `{ cap: [prerequisite, …] }`.
 *
 * `platform.users.ban` is used only in the Users section and the user modal,
 * which `platform.users.view` opens — a user granted the ban alone got an empty
 * `/admin` page. The capability editor (PlatformAdmin.jsx) grants the
 * prerequisite with the cap and revokes the cap with the prerequisite, and
 * `updateUserPlatformCaps` refuses a result that breaks a row; both apply it
 * through `capDependencies.js`. Neither the resolver nor `firestore.rules`
 * reads it: it guards the write path only.
 */
export const PLATFORM_CAP_REQUIRES = Object.freeze({
  'platform.users.ban': Object.freeze(['platform.users.view']),
});
